// Aircall voice: AI calls, lifecycle, live actions (PRD §11).
import { z } from 'zod';
import * as V from './api-voice.js';
import { IdParam, route, W, Ws } from './route-def.js';

export const voiceRoutes = {
  // ───────── Agent management gate (admin) ─────────
  getVoiceSetup: route({
    method: 'GET', path: '/workspaces/:workspaceId/voice/setup', summary: 'Configuración de voz: capacidades, líneas, agentes de Aircall y despliegues', tag: 'voice',
    minRole: 'manager', params: Ws, response: V.VoiceSetupSchema,
  }),
  updateVoiceSettings: route({
    method: 'PUT', path: '/workspaces/:workspaceId/voice/connections/:connectionId', summary: 'Agentes de IA de Aircall por línea/proyecto y transporte de vinculación', tag: 'voice',
    minRole: 'admin', params: W({ connectionId: IdParam }), body: V.UpdateVoiceSettingsSchema, response: V.VoiceConnectionSetupSchema,
  }),
  rotateVoiceActionSecret: route({
    method: 'POST', path: '/workspaces/:workspaceId/voice/connections/:connectionId/action-secret', summary: 'Generar secreto de acciones en vivo (visible una vez)', tag: 'voice',
    minRole: 'admin', params: W({ connectionId: IdParam }), response: V.ActionSecretSchema,
  }),
  registerAircallWebhook: route({
    method: 'POST', path: '/workspaces/:workspaceId/voice/connections/:connectionId/webhook', summary: 'Registrar el webhook de Aircall (API o token copiado de Aircall)', tag: 'voice',
    minRole: 'admin', params: W({ connectionId: IdParam }), body: V.RegisterAircallWebhookSchema, response: V.VoiceConnectionSetupSchema,
  }),
  exportVoiceSetupPackage: route({
    method: 'GET', path: '/workspaces/:workspaceId/voice/connections/:connectionId/agents/:mappingId/package', summary: 'Paquete de configuración para Aircall (prompt compilado y acciones)', tag: 'voice',
    minRole: 'admin', params: W({ connectionId: IdParam, mappingId: IdParam }), response: V.VoiceSetupPackageSchema,
  }),
  confirmVoiceDeployment: route({
    method: 'POST', path: '/workspaces/:workspaceId/voice/deployments/:deploymentId/confirm', summary: 'Confirmé en Aircall: marcar la versión como aplicada', tag: 'voice',
    minRole: 'admin', params: W({ deploymentId: IdParam }), body: V.ConfirmVoiceDeploymentSchema, response: V.VoiceDeploymentSchema,
  }),
  setVoiceCapability: route({
    method: 'POST', path: '/workspaces/:workspaceId/voice/connections/:connectionId/capabilities', summary: 'Registrar el resultado de una verificación real de Fase 0 (operador de plataforma)', tag: 'voice',
    minRole: 'admin', capability: 'platform_prompt_admin', params: W({ connectionId: IdParam }), body: V.SetVoiceCapabilitySchema, response: V.VoiceConnectionSetupSchema,
  }),
  testVoiceActionAuth: route({
    method: 'POST', path: '/workspaces/:workspaceId/voice/connections/:connectionId/test-action-auth', summary: 'Comprobar el secreto de acciones y un token de sesión', tag: 'voice',
    minRole: 'admin', params: W({ connectionId: IdParam }), body: V.TestVoiceActionAuthSchema, response: V.TestVoiceActionAuthResultSchema,
  }),
  voiceReadiness: route({
    method: 'GET', path: '/workspaces/:workspaceId/voice/readiness', summary: 'Si se puede llamar ahora y por qué no', tag: 'voice',
    minRole: 'advisor', params: Ws, response: V.VoiceReadinessSchema,
  }),
  voiceServiceStatus: route({
    method: 'GET', path: '/workspaces/:workspaceId/voice/service', summary: 'Estado del servicio de voz propio: alcanzable, telefonía, modelo asignado', tag: 'voice',
    minRole: 'manager', params: Ws, response: V.VoiceServiceStatusSchema,
  }),
  createVoiceWebSession: route({
    method: 'POST', path: '/workspaces/:workspaceId/voice/web-sessions', summary: 'Sesión de prueba en el navegador con el servicio de voz propio (sin teléfono; herramientas en modo de prueba)', tag: 'voice',
    minRole: 'admin', params: Ws, body: V.CreateVoiceWebSessionSchema, response: V.VoiceWebSessionSchema, status: 201,
  }),
  voiceLatencyStats: route({
    method: 'GET', path: '/workspaces/:workspaceId/voice/latency', summary: 'Latencia p50/p95 de acciones en vivo por herramienta', tag: 'voice',
    minRole: 'manager', params: Ws, query: V.VoiceLatencyQuerySchema, response: V.VoiceLatencyStatsSchema,
  }),

  // ───────── Calls ─────────
  startAiCall: route({
    method: 'POST', path: '/workspaces/:workspaceId/calls', summary: 'Llamar ahora con el agente de IA (202 = aceptada por el proveedor, no contestada)', tag: 'calls',
    minRole: 'advisor', params: Ws, body: V.StartAiCallSchema, response: V.StartAiCallResultSchema, status: 202,
  }),
  getCallDetail: route({
    method: 'GET', path: '/workspaces/:workspaceId/calls/:callId', summary: 'Detalle de llamada: intentos, eventos, transcripción y análisis', tag: 'calls',
    minRole: 'advisor', params: W({ callId: IdParam }), response: V.CallDetailSchema,
  }),
  reconcileCall: route({
    method: 'POST', path: '/workspaces/:workspaceId/calls/:callId/reconcile', summary: 'Conciliar con Aircall un intento incierto (nunca vuelve a llamar)', tag: 'calls',
    minRole: 'advisor', params: W({ callId: IdParam }), response: V.CallDetailSchema,
  }),
  getCallRecording: route({
    method: 'GET', path: '/workspaces/:workspaceId/calls/:callId/recording', summary: 'Enlace temporal a la grabación (requiere permiso de grabaciones)', tag: 'calls',
    minRole: 'advisor', params: W({ callId: IdParam }), response: V.CallRecordingSchema,
  }),

  // ───────── Machine routes (Aircall → us; never cookies) ─────────
  aircallWebhook: route({
    method: 'POST', path: '/providers/aircall/:connectionId/webhooks', summary: 'Webhook de Aircall (token de webhook verificado)', tag: 'ingress',
    public: 'ingress', params: z.object({ connectionId: IdParam }), response: V.AircallWebhookAckSchema,
  }),
  aircallLiveAction: route({
    method: 'POST', path: '/providers/aircall/:connectionId/actions/:action', summary: 'Acción en vivo del agente de voz (secreto de conexión + token de sesión)', tag: 'ingress',
    public: 'ingress', params: z.object({ connectionId: IdParam, action: V.VoiceLiveActionSchema }), response: V.VoiceLiveActionResponseSchema,
  }),

  // ───────── Machine routes (own voice service → us; HMAC + session binding; see docs/voice-service.md) ─────────
  voiceSessionBootstrap: route({
    method: 'POST', path: '/voice/sessions/bootstrap', summary: 'Instrucciones de voz compiladas, contexto, herramientas y modelo para una sesión', tag: 'ingress',
    public: 'ingress', response: V.VoiceSessionBootstrapSchema,
  }),
  voiceSessionAction: route({
    method: 'POST', path: '/voice/sessions/actions/:action', summary: 'Acción en vivo de una sesión del servicio de voz', tag: 'ingress',
    public: 'ingress', params: z.object({ action: V.VoiceLiveActionSchema }), response: V.VoiceLiveActionResponseSchema,
  }),
  voiceSessionEvents: route({
    method: 'POST', path: '/voice/sessions/events', summary: 'Eventos de la sesión: estado, turnos, latencia, uso', tag: 'ingress',
    public: 'ingress', body: V.VoiceSessionEventsSchema, response: V.VoiceSessionEventsAckSchema,
  }),
  voiceSessionInbound: route({
    method: 'POST', path: '/voice/sessions/inbound', summary: 'Llamada entrante en el punto de entrada de telefonía', tag: 'ingress',
    public: 'ingress', body: V.VoiceInboundRequestSchema, response: V.VoiceInboundResponseSchema,
  }),
  voiceSessionStream: route({
    method: 'POST', path: '/voice/sessions/stream', summary: 'Canje del ticket de audio de una llamada de Telnyx controlada por LIA (un solo uso)', tag: 'ingress',
    public: 'ingress', body: V.VoiceStreamRequestSchema, response: V.VoiceStreamResponseSchema,
  }),
  voiceSessionHangup: route({
    method: 'POST', path: '/voice/sessions/hangup', summary: 'Colgar en Telnyx una llamada controlada por LIA (a pedido del servicio de voz)', tag: 'ingress',
    public: 'ingress', response: V.VoiceHangupResponseSchema,
  }),
  copilotStreamRedeem: route({
    method: 'POST', path: '/voice/sessions/transcription/stream', summary: 'Canje del ticket de audio de una llamada de asesor en Telnyx para el copiloto (un solo uso; solo transcripción)', tag: 'ingress',
    public: 'ingress', body: V.CopilotStreamRedeemSchema, response: V.CopilotStreamRedeemResponseSchema,
  }),
  copilotStreamUtterances: route({
    method: 'POST', path: '/voice/sessions/transcription/utterances', summary: 'Fragmentos finales transcritos de una llamada de asesor en Telnyx (token del flujo)', tag: 'ingress',
    public: 'ingress', body: V.CopilotStreamUtterancesSchema, response: V.CopilotStreamUtterancesAckSchema,
  }),
} as const;
